import { evaluate } from "@kyan/ui";
import { useDebounceValue } from "usehooks-ts";
import { z } from "zod";

import {
  getPerpConstraints,
  getPerpSizeDisplayPrecision,
  perpRfqSizes,
} from "#/config/orders";
import { useUserScopes } from "#/services/dynamic/hooks/useUserScopes";
import { useIsDevMode } from "#/store/app/devMode/devMode";
import { useMarketIndexPrice } from "#/store/app/indexPrices/marketIndexPrice";
import { useMarket } from "#/store/app/portfolio/market";
import { usePerpLimitPrice } from "#/store/perps/tradePanel/perpLimitPrice";
import { usePerpSize } from "#/store/perps/tradePanel/perpSize";
import { usePerpUnit } from "#/store/perps/tradePanel/perpUnit";
import { type UnderlyingSymbol } from "#/types/markets";
import { match } from "#/utils/match";
import { roundToNearest } from "#/utils/number.utils";
import { formatCompact } from "#/utils/valueFormatters";

import { useThrottle } from "./useThrottle";
import { useValidateMaxNumOfOrders } from "./useValidateMaxNumOfOrders";

const createSchema = ({
  minSize,
  underlying,
  isUnderlyingSelected,
  isShort,
  isRfq,
}: {
  minSize: number;
  underlying: UnderlyingSymbol;
  isUnderlyingSelected: boolean;
  isRfq?: boolean;
  isShort?: boolean;
}) => {
  const minimumText = isShort ? `Min` : `Minimum`;
  const sizeDisplayPrecision = getPerpSizeDisplayPrecision(
    underlying,
    isUnderlyingSelected,
  );
  const minimumSizeText = formatCompact(minSize, {
    maximumFractionDigits: sizeDisplayPrecision,
  });

  return z.coerce.number().min(minSize, {
    message: evaluate({
      [`Min RFQ size for ${underlying} is $${minimumSizeText}`]: isRfq,
      [`${minimumText} size is ${minimumSizeText}`]: isUnderlyingSelected,
      [`${minimumText} size is ~$${minimumSizeText}`]: true,
    }),
  });
};

export const validatePerpSize = ({
  size,
  underlying,
  hasNoGuardRails = false,
  price = NaN,
  isRfq = false,
  isShort = false,
  isUnderlyingSelected = false,
}: {
  size: string;
  underlying: UnderlyingSymbol;
  hasNoGuardRails: boolean;
  price?: number;
  isRfq?: boolean;
  isShort?: boolean;
  isUnderlyingSelected?: boolean;
}) => {
  if (!size)
    return {
      minSize: undefined,
      sizeError: "",
      hasSizeError: false,
      sizeWarning: "",
    };

  const {
    minSize: minPerpSize,
    sizeDisplayPrecisionNotional,
    maxSizeNotional,
  } = getPerpConstraints(underlying);
  const minSize = match([
    [perpRfqSizes[underlying][0], isRfq],
    [minPerpSize, isUnderlyingSelected],
    [
      isFinite(price)
        ? roundToNearest(
            minPerpSize * price,
            Math.pow(10, -sizeDisplayPrecisionNotional),
            false,
          )
        : 0,
      true,
    ],
  ])!;

  const schema = createSchema({
    underlying,
    minSize,
    isUnderlyingSelected,
    isShort,
    isRfq,
  });
  const result = schema.safeParse(size);

  const sizeError = result.success
    ? ""
    : (result.error.issues[0]?.message ?? "");

  const numericSize = Number(size);
  const notional = isUnderlyingSelected
    ? numericSize * (isFinite(price) ? price : 0)
    : numericSize;
  const maxSizeNotionalText = formatCompact(maxSizeNotional);

  const sizeWarning =
    notional > maxSizeNotional && !hasNoGuardRails
      ? `Order size exceeds the $${maxSizeNotionalText} limit. ${isShort ? "" : "Adjust the amount."}`
      : "";

  return {
    minSize,
    sizeError,
    hasSizeError: !result.success,
    sizeWarning,
    hasSizeWarning: Boolean(sizeWarning),
  };
};

export const useValidatePerpSize = ({
  isRfq = false,
  isShort = false,
  isLimit = false,
}: {
  isRfq?: boolean;
  isShort?: boolean;
  isLimit?: boolean;
} = {}) => {
  const {
    market: { underlying },
  } = useMarket();
  const { marketIndexPrice } = useMarketIndexPrice();
  const { isUnderlyingSelected } = usePerpUnit();
  const { perpSizeInputValue } = usePerpSize();
  const { perpLimitPrice } = usePerpLimitPrice();
  const { hasEarlyAccessVip } = useUserScopes();
  const { isDevMode } = useIsDevMode();
  const [debouncedSize] = useDebounceValue(perpSizeInputValue, 800);
  const [debouncedLimitPrice] = useDebounceValue(perpLimitPrice, 800);
  const throttledIndexPrice = useThrottle(marketIndexPrice, 800);
  const hasNoGuardRails = isDevMode || hasEarlyAccessVip;
  const maxNumOfOrdersError = useValidateMaxNumOfOrders(
    underlying,
    debouncedSize,
    isLimit,
  );

  if (maxNumOfOrdersError && !hasNoGuardRails)
    return {
      sizeError: maxNumOfOrdersError,
      hasSizeError: true,
      sizeWarning: "",
      hasSizeWarning: false,
      minSize: undefined,
    };

  const limitPriceNum = Number(debouncedLimitPrice);
  const effectivePrice =
    isLimit && limitPriceNum > 0 ? limitPriceNum : throttledIndexPrice;

  return validatePerpSize({
    size: debouncedSize,
    price: effectivePrice,
    underlying,
    hasNoGuardRails,
    isUnderlyingSelected,
    isRfq,
    isShort,
  });
};
